import {
  Region,
  useAPIGetRegionDetail,
  useAPIGetRegions,
} from 'api/region/useAPIRegion'
import React from 'react'
import { Session } from 'types/auth'
import { RegionType } from 'types/region-type'

import { BCKS_SESSION_STORAGE_KEY } from 'configs/auth'
import { BCKS_SUPERADMIN, BCKS_UPT_PREFIX } from 'configs/roles'

import { useLocalStorage } from '../useLocalStorage'
import { useRoles } from '../useRoles'

export function useRegionHook() {
  const { data: localStorageSession, set: setLocalStorageSession } =
    useLocalStorage<Session>(BCKS_SESSION_STORAGE_KEY, null)
  const { userRegion } = localStorageSession
  const { userRoles } = useRoles()
  const { data, isLoading: isLoadingRegions } = useAPIGetRegions()
  const { data: regionDetail, isLoading: isLoadingRegionDetail } =
    useAPIGetRegionDetail(userRegion?.id, {
      type: userRegion?.type,
    })

  const setSelectedRegion = (selectedRegion) => {
    setLocalStorageSession({
      ...localStorageSession,
      userRegion: selectedRegion,
    })
  }

  const userProvinceIds = React.useMemo(
    () =>
      userRoles
        .filter((role) => role.startsWith(BCKS_UPT_PREFIX))
        .map((role) => role.split('_').pop()),
    [userRoles]
  )

  const userScopedRegionData: Region[] = React.useMemo(
    () =>
      data?.regions.filter((region) =>
        !userRoles.includes(BCKS_SUPERADMIN) && userProvinceIds.length
          ? userProvinceIds.includes(region.province.id)
          : true
      ) ?? [],
    [data, userRoles, userProvinceIds]
  )

  const getUserScopedFlattenProvinceData = React.useMemo(
    () =>
      userScopedRegionData.map((region) => ({
        id: region.province.id,
        name: region.province.name,
        type: RegionType.PROVINSI,
      })) ?? [],
    [userScopedRegionData]
  )

  const getUserScopedFlattenCityData = React.useMemo(
    () =>
      userScopedRegionData
        .map((region) =>
          region.cities.map((city) => ({
            id: city.id,
            name: city.name,
            type: RegionType.KABUPATEN,
          }))
        )
        .flat() ?? [],
    [userScopedRegionData]
  )

  const getFlattenProvinceData = React.useMemo(
    () =>
      data?.regions?.map((region) => ({
        id: region.province.id,
        name: region.province.name,
        type: RegionType.PROVINSI,
      })) ?? [],
    [data]
  )

  const getFlattenCityData = React.useMemo(
    () =>
      data?.regions
        ?.map((region) =>
          region.cities.map((city) => ({
            id: city.id,
            name: city.name,
            type: RegionType.KABUPATEN,
          }))
        )
        .flat() ?? [],
    [data]
  )

  const userScopedRegionDataByIdAndNameOnly = React.useMemo(
    () => [
      ...getUserScopedFlattenProvinceData,
      ...getUserScopedFlattenCityData,
    ],
    [getUserScopedFlattenProvinceData, getUserScopedFlattenCityData]
  )

  const regionDataByIdAndNameOnly = React.useMemo(
    () => [...getFlattenProvinceData, ...getFlattenCityData],
    [getFlattenProvinceData, getFlattenCityData]
  )

  const getRegionNameByRegionId = React.useCallback(
    (regionId) => {
      return (
        regionDataByIdAndNameOnly.find((region) => region.id === regionId)
          ?.name ?? ''
      )
    },
    [regionDataByIdAndNameOnly]
  )

  const getRegionTypeByRegionId = React.useCallback(
    (regionId) => {
      return (
        regionDataByIdAndNameOnly.find((region) => region.id === regionId)
          ?.type ?? ''
      )
    },
    [regionDataByIdAndNameOnly]
  )

  return {
    isLoadingRegions,
    regionData: userScopedRegionData,
    provinceData: getUserScopedFlattenProvinceData,
    allRegionData: userScopedRegionDataByIdAndNameOnly,
    getRegionNameByRegionId,
    getRegionTypeByRegionId,
    isLoadingRegionDetail,
    regionDetail: regionDetail || {
      id: userRegion?.id,
      type: userRegion?.type,
      name: userRegion?.name,
    },
    setSelectedRegion,
    resetSelectedRegion: () =>
      setSelectedRegion({
        id: '',
        type: '',
      }),
  }
}
